import React, { useCallback, useContext, useMemo, useState } from 'react';
import { makeStyles, Theme, useTheme } from '@material-ui/core';
import clsx from 'clsx';
import { ritzDataContext, selectLoggedInAgent, selectProjectAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { modeUIContext, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { selectHomeKVStore, userAppDataContext } from '@moderepo/mode-data-state-user-app';
import { BaseInfoCompProps, capitalizeAll, GenericMessageBox, useModePanelStyle } from '@moderepo/mode-app-components';
import ReactGridLayout, { Layout } from 'react-grid-layout';
import { Agent, AgentRole } from '@moderepo/mode-internal-apis';
import { DashboardWidgetType, DASHBOARD_COLS, DASHBOARD_WIDGET_MIN_WIDTH, HOME_DASHBOARDS_KV_KEY, isDashboardWidgetSettings,
    isHomeDashboardsKeyValuePair, isProjectDashboardsSettings, ResponsiveGridLayout, WidgetGroupWidgetSettings, WIDGET_GROUP_ROW_HEIGHT,
    WIDGET_GROUP_WIDGETS_SPACING, WIDGET_GROUP_WIDGET_MIN_HEIGHT } from './dashboardTypes';
import { useRitzTranslation } from '../../utils/customHooks';
import { DashboardWidgetPreviewCompOptions, SlideOutPanelCompType } from '../routes/defaultProjectConfig';
import { WidgetGroupWidgetViewComp, WidgetGroupWidgetViewCompProps } from '../../components/dashboard';
import { DashboardWidgetContainer } from './DashboardWidgetContainer';
import { SelectDashboardWidgetTypeDialog, SelectDashboardWidgetTypeDialogProps } from './SelectDashboardWidgetTypeDialog';
import { getWidgetsLayout, handleWidgetResizedOrMoved } from './dashboardUtils';
import { isAlertingFeatureEnabled, isEntitiesFeatureEnabled } from '../../utils/utils';



const useStyle = makeStyles((theme: Theme) => {
    return {
        widget: {
            width : '100%',
            height: '100%',
        },

        widgetViewContent: {
            width  : '100%',
            height : '100%',
            padding: theme.spacing(2),
        },

        loadingContainer: {
            position      : 'absolute',
            width         : '100%',
            height        : '100%',
            zIndex        : 1,
            top           : 0,
            left          : 0,
            background    : 'rgba(0, 0, 0, 0.3)',
            display       : 'flex',
            alignItems    : 'center',
            justifyContent: 'center',
            color         : '#ffffff',
        },

        emptyListMessage: {
            flex          : 1,
            alignItems    : 'center',
            justifyContent: 'center',
        },
    };
}, { name: 'WidgetGroupWidgetDataViewContainer', index: 2 });



export interface WidgetGroupWidgetViewContainerProps {
    readonly className?: string | undefined;
    readonly projectId: number;
    readonly dashboardId: number;
    readonly selectedWidget: WidgetGroupWidgetSettings;
    readonly isInMoveAndResizeMode?: boolean | undefined;

    // The handler for when the widget itself wants to enable move and resize mode. The widget itself doesn't control the mode so it will
    // call the handler to handle the event
    readonly enableMoveAndResizeModeHandler?: ()=> void;

    // This is a container that wraps a DUMB component and some time we need to pass some props such as className, styles, etc... into the
    // component from the container of this container so this is a way for us to pass props into the component
    readonly infoCompProps?: {
        readonly className?: BaseInfoCompProps['className'] | undefined;
    } | undefined;


    /**
     * The context of which page the user is on. Because this component can be shown in different pages, on a Project page where there is no
     * user or home context or on a User's Home page which has user and home context. This data is not required but it helps create a better
     * URL when we take the user to another page.
     */
    readonly routeContext?: {
        readonly userId?: number | undefined;
        readonly homeId?: number | undefined;
    }
}



/**
 * This is the component for displaying the UI/data view for the WidgetGroupDashboardWidget.
 */
export const WidgetGroupWidgetViewContainer: React.FC<WidgetGroupWidgetViewContainerProps> = (
    props: WidgetGroupWidgetViewContainerProps,
) => {

    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    // Get the logged in Agent data for the given project. This is is not the same as loggedInAgent data.
    const projectAgent = loggedInAgent ? selectProjectAgent(dataState, project?.id, loggedInAgent.id) : undefined;
    const canEditDashboard = projectAgent?.role === AgentRole.ADMIN;

    // Check if entities and alerting features enabled. We need these feature to enable the Alerts dashboard widget
    const entityFeatureEnabled = isEntitiesFeatureEnabled(project, loggedInAgent);
    const alertingFeatureEnabled = isAlertingFeatureEnabled(project, loggedInAgent);


    // Select all the dashboards for the Home/Project from the data state
    const dashboards = useMemo(() => {
        if (props.routeContext?.homeId) {
            // Get dashboards settings from Home KV
            const dashboardKV = selectHomeKVStore(appDataState, props.routeContext.homeId, HOME_DASHBOARDS_KV_KEY);
            if (isHomeDashboardsKeyValuePair(dashboardKV)) {
                return dashboardKV.value;
            }
        } else if (project && isProjectDashboardsSettings(project.supportToolSettings.dashboardSettings)) {
            // Get dashboards settings from Project settings
            return project.supportToolSettings.dashboardSettings.dashboards;
        }

        return [];
    }, [appDataState, project, props.routeContext]);


    // Select the dashboard object that this Widget Group belonged to
    const selectedDashboard = useMemo(() => {
        if (dashboards) {
            return dashboards.find((dashboard) => {
                return dashboard.id === props.dashboardId;
            });
        }
        return undefined;
    }, [dashboards, props.dashboardId]);


    // Select all the widgets that are placed in this Widget Group
    const displayableWidgets = useMemo(() => {
        if (selectedDashboard) {
            return selectedDashboard.widgets?.filter((widget) => {
                // Return true if the widget.widgetGroupId is this Widget Group's ID.
                // To make sure we don't put a widget group inside itself, we also need to make sure the widget.id is NOT this widget group's id
                return widget.widgetGroupId === props.selectedWidget.id && widget.id !== props.selectedWidget.id;
            });
        }
        return undefined;
    }, [props.selectedWidget.id, selectedDashboard]);


    /**
     * Create the layouts object for all the widgets based on the widgets' position and sizes. The React Grid Layout needs this object
     * to layout the widgets.
     * We will only create the layout for the widgets that are inside THIS widget group
     */
    const widgetsLayouts = useMemo((): ReactGridLayout.Layouts => {
        return getWidgetsLayout(displayableWidgets, DASHBOARD_WIDGET_MIN_WIDTH, WIDGET_GROUP_WIDGET_MIN_HEIGHT);
    }, [displayableWidgets]);



    const { trans } = useRitzTranslation(project);
    const classes = useStyle();
    const panelClasses = useModePanelStyle();
    const theme = useTheme();
    const [showSelectDashboardWidgetTypeDialog, setShowSelectDashboardWidgetTypeDialog] = useState<boolean>(false);


    /**
     * On Show Dashboard Widget settings button clicked, we will show the dashboard widget's settings in a Slide-out panel
     */
    const showDashboardWidgetPreview = useCallback(() => {
        const options: DashboardWidgetPreviewCompOptions = {
            compType : SlideOutPanelCompType.DASHBOARD_WIDGET_PREVIEW,
            location : SlideOutPanelLocation.RIGHT,
            compProps: {
                data: {
                    projectId     : props.projectId,
                    dashboardId   : props.dashboardId,
                    selectedWidget: props.selectedWidget.id,
                },
                routeContext: props.routeContext,
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.routeContext, props.dashboardId, props.projectId, props.selectedWidget.id, uiDispatch]);



    /**
     * Create the Actions to show in the widget's panel header, the top right corner.
     */
    const actions = useMemo((): WidgetGroupWidgetViewCompProps['customSecondaryActions'] => {
        if (canEditDashboard && !props.isInMoveAndResizeMode) {
            return [{
                icon   : 'add',
                label  : capitalizeAll(trans('dashboard.action.add_dashboard_widget')),
                onClick: () => {
                    setShowSelectDashboardWidgetTypeDialog(true);
                },
            }, {
                icon   : 'settings',
                label  : capitalizeAll(trans('label.settings')),
                onClick: showDashboardWidgetPreview,
            },
            {
                icon   : 'open_with',
                label  : capitalizeAll(trans('dashboard.action.move_and_resize_widgets')),
                onClick: () => {
                    if (props.enableMoveAndResizeModeHandler) {
                        props.enableMoveAndResizeModeHandler();
                    }
                },
            }];
        }
        return undefined;
    }, [canEditDashboard, props, showDashboardWidgetPreview, trans]);


    /**
     * on Widget resized or moved, call the handler to handle the event
     */
    const onWidgetResizedOrMoved = useCallback((layouts: Layout[]) => {
        handleWidgetResizedOrMoved({
            layouts,
            uiDispatch,
            dataState,
            dataDispatch,
            appDataState,
            appDataDispatch,
            loggedInAgent,
            projectId  : props.projectId,
            homeId     : props.routeContext?.homeId,
            dashboardId: props.dashboardId,
            trans,
            widgets    : displayableWidgets,
        });
    }, [appDataDispatch, appDataState, dataDispatch, dataState, displayableWidgets, loggedInAgent, props.dashboardId, props.projectId,
        props.routeContext, trans, uiDispatch]);



    /**
     * On create widget clicked, we will use the slide out panel to show the UI for creating a widget
     */
    const onCreateWidgetClick = useCallback((widgetType: DashboardWidgetType) => {
        const options: DashboardWidgetPreviewCompOptions = {
            compType : SlideOutPanelCompType.DASHBOARD_WIDGET_PREVIEW,
            location : SlideOutPanelLocation.RIGHT,
            compProps: {
                data: {
                    projectId     : props.projectId,
                    dashboardId   : props.dashboardId,
                    selectedWidget: widgetType,
                    widgetGroupId : props.selectedWidget.id,
                },
                routeContext: props.routeContext,
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.projectId, props.dashboardId, props.selectedWidget.id, props.routeContext, uiDispatch]);



    /**
     * Alerts Dashboard widget requires Alerting feature which requires Entities feature therefore we will not enable the Alerts Dashboard
     * widget type if one of these features is not enabled.
     */
    const excludedWidgetTypes = useMemo((): SelectDashboardWidgetTypeDialogProps['excludedWidgetTypes'] => {
        // The list of widget type we won't allow the user to create from this widget. Because this is a Widget Group, we can't let user
        // create widget of type Widget Group
        const excludedWidgetTypes: DashboardWidgetType[] = [DashboardWidgetType.WIDGET_GROUP];

        if (!(entityFeatureEnabled)) {
            excludedWidgetTypes.push(DashboardWidgetType.ENTITIES_MAP);
        }
        if (!(entityFeatureEnabled && alertingFeatureEnabled)) {
            excludedWidgetTypes.push(DashboardWidgetType.ALERTS);
        }
        return excludedWidgetTypes;
    }, [alertingFeatureEnabled, entityFeatureEnabled]);


    return (
        <WidgetGroupWidgetViewComp
            className={clsx(classes.widget, props.className, props.infoCompProps?.className, classes.widget)}
            title={props.selectedWidget.title}
            icon={props.selectedWidget.icon}
            // Always showBackground if we are in Move/Resize mode. Otherwise, show background based on the widget.showBackground
            showBackground={props.isInMoveAndResizeMode || !displayableWidgets?.length || props.selectedWidget.showBackground}
            customSecondaryActions={actions}
            showCustomActionOnHover
        >
            {displayableWidgets && displayableWidgets.length > 0 ? (
                <ResponsiveGridLayout
                    margin={[WIDGET_GROUP_WIDGETS_SPACING, WIDGET_GROUP_WIDGETS_SPACING]}
                    cols={DASHBOARD_COLS}
                    breakpoints={{ lg: 0 }}
                    rowHeight={WIDGET_GROUP_ROW_HEIGHT}
                    layouts={widgetsLayouts}
                    isResizable={props.isInMoveAndResizeMode}
                    isDraggable={props.isInMoveAndResizeMode}
                    onResizeStop={onWidgetResizedOrMoved}
                    onDragStop={onWidgetResizedOrMoved}
                    draggableHandle={`.${panelClasses.panelHeader}`}
                >
                    {displayableWidgets.map((widget, index) => {

                        // Make sure widget data is valid first because bad widget settings can break the app.
                        if (!isDashboardWidgetSettings(widget)) {
                            // eslint-disable-next-line no-console
                            console.log('Invalid widget config', widget);
                            // eslint-disable-next-line react/no-array-index-key
                            return <div key={index.toString()} />;
                        }

                        return (
                            <div key={widget.id.toString()}>
                                <DashboardWidgetContainer
                                    projectId={props.projectId}
                                    dashboardId={props.dashboardId}
                                    selectedWidget={widget.id}
                                    isInMoveAndResizeMode={props.isInMoveAndResizeMode}
                                    enableMoveAndResizeModeHandler={props.enableMoveAndResizeModeHandler}
                                    infoCompProps={{
                                        className: clsx(classes.widget),
                                    }}
                                    routeContext={props.routeContext}
                                />
                            </div>
                        );
                    })}
                </ResponsiveGridLayout>
            ) : (
                <GenericMessageBox
                    className={clsx(classes.emptyListMessage)}
                    title={trans('generic.no_data.title', {
                        term: capitalizeAll(trans('label.dashboard_widget', { count: 2 })),
                    })}
                    action={trans('dashboard.action.add_dashboard_widget')}
                    actionHandler={() => {
                        setShowSelectDashboardWidgetTypeDialog(true);
                    }}
                />
            )}


            <SelectDashboardWidgetTypeDialog
                open={showSelectDashboardWidgetTypeDialog}
                excludedWidgetTypes={excludedWidgetTypes}
                projectId={props.projectId}
                onCancel={() => {
                    setShowSelectDashboardWidgetTypeDialog(false);
                }}
                onWidgetTypeSelected={(type) => {
                    setShowSelectDashboardWidgetTypeDialog(false);
                    onCreateWidgetClick(type);
                }}
            />
        </WidgetGroupWidgetViewComp>
    );
};
